import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';

const APP_META = {
  gmail:        { icon: '✉️', iconBg: 'rgba(234,67,53,0.08)', iconColor: '#EA4335', scopes: ['Send', 'Read', 'Draft'] },
  googlecalendar: { icon: '📅', iconBg: 'rgba(66,133,244,0.08)', iconColor: '#4285F4', scopes: ['Events', 'Free Slots'] },
  googledrive:  { icon: '📁', iconBg: 'rgba(15,157,88,0.08)', iconColor: '#0F9D58', scopes: ['Files', 'Folders', 'Share'] },
  googledocs:   { icon: '📄', iconBg: 'rgba(66,133,244,0.08)', iconColor: '#4285F4', scopes: ['Create', 'Edit', 'Export'] },
  googlesheets: { icon: '📊', iconBg: 'rgba(15,157,88,0.08)', iconColor: '#0F9D58', scopes: ['Read', 'Write', 'Append'] },
  outlook:      { icon: '📨', iconBg: 'rgba(0,120,212,0.08)', iconColor: '#0078D4', scopes: ['Read', 'Draft', 'Send'] },
};

const DEFAULT_META = { icon: '🔗', iconBg: 'rgba(0,0,0,0.06)', iconColor: '#666', scopes: [] };

export default function useComposioApps() {
  const [apps, setApps] = useState([]);
  const [statuses, setStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    // Apps and connections load independently: a failing connections call (e.g. the Composio key is
    // not armed on this env) must show as an error, not blank the whole page.
    const [appsRes, connRes] = await Promise.allSettled([api.getComposioApps(), api.getComposioConnections()]);
    const appList = (appsRes.status === 'fulfilled' ? appsRes.value?.apps || [] : []).map(a => ({
      ...a,
      ...(APP_META[a.key] || DEFAULT_META),
    }));
    setApps(appList);
    const newStatuses = {};
    for (const a of appList) {
      newStatuses[a.key] = { connected: false, status: 'NOT_CONNECTED', connection_id: null };
    }
    for (const conn of connRes.status === 'fulfilled' ? connRes.value || [] : []) {
      if (conn.app && newStatuses[conn.app] !== undefined && conn.status === 'ACTIVE') {
        newStatuses[conn.app] = { connected: true, status: conn.status, connection_id: conn.id };
      }
    }
    setStatuses(newStatuses);
    const failed = [appsRes, connRes].find(r => r.status === 'rejected');
    if (failed) {
      console.error('Failed to load Composio apps/connections:', failed.reason);
      setError(`Could not read connections: ${failed.reason?.message || 'request failed'}`);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  return { apps, statuses, loading, error, reload: load };
}
